<a class='main-nav-link focus-fix {selected ? "selected" : ""}' aria-label={ariaLabel} aria-current={selected}
  on:click="onClick(event)" rel="prefetch" {href}>
  <div class="nav-icon-and-label">
    <NavItemIcon {showBadge} {badgeNumber} {svg} />
    <span class="nav-link-label">{label}</span>
  </div>
  <div class="nav-indicator-wrapper" ref:indicator>
    <div class="nav-indicator"></div>
  </div>
</a>
<style>
  .main-nav-link {
    text-decoration: none;
    display: flex;
    justify-content: center;
    align-items: center;
    flex: 1;
    flex-direction: column;
    position: relative;
  }

  .nav-icon-and-label {
    padding: var(--nav-icon-pad-v) var(--nav-icon-pad-h);
    display: flex;
    justify-content: center;
    align-items: center;
    flex: 1;
    transition: transform 111ms ease;
  }

  .main-nav-link.selected {
    background: var(--nav-a-selected-bg);
  }

  .main-nav-link.selected:hover {
    background: var(--nav-a-selected-bg-hover);
  }

  .nav-indicator-wrapper {
    height: var(--nav-indicator-height);
    display: flex;
    position: absolute;
    bottom: 0;
    inset-inline: 0;
    z-index: 1;
  }

  .nav-indicator {
    flex: 1;
    border-top-right-radius: 1rem;
    border-top-left-radius: 1rem;
  }

  .main-nav-link .nav-indicator {
    background: var(--nav-indicator-bg-active);
    transform: translateY(100%);
    transition: transform 111ms ease;
  }

  .main-nav-link.selected .nav-indicator {
    transform: translateY(0%) !important;
  }

  .main-nav-link.selected .nav-icon-and-label {
    transform: translateY(calc(var(--nav-indicator-height) * -0.5));
  }

  .main-nav-link:hover {
    background-color: var(--nav-a-bg-hover);
    text-decoration: none;
  }

  .main-nav-link:hover .nav-link-label {
    color: var(--nav-text-color-hover);
  }

  .main-nav-link:active {
    background-color: var(--nav-active-bg);
  }

  .main-nav-link.selected:active {
    background-color: var(--nav-a-selected-active-bg);
  }

  .nav-link-label {
    font-size: var(--nav-font-size);
    line-height: var(--nav-icon-size);
    color: var(--nav-text-color);
    padding-inline-start: 10px;
    white-space: nowrap;
    overflow: visible;
    text-overflow: ellipsis;
  }

  @media (max-width: 991px) {
    .main-nav-link .nav-link-label {
      /* Copied from the sr-only styles in global.scss
       * the reason we explicitly leave this <span> in is because Voice Control on iOS does not
       * understand aria-labels very well, but it understands hidden text just fine
       */
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      border: 0;
    }
  }
</style>
<script>
  import NavItemIcon from './NavItemIcon.html'
  import { store } from '../_store/store.js'
  import { on, emit } from '../_utils/eventBus.ts'
  import { mark, stop } from '../_utils/marks.js'
  import { scrollToTop } from '../_utils/scrollToTop.js'
  import { normalizePageName } from '../_utils/normalizePageName.js'
  import { formatIntl } from '../_utils/formatIntl.js'

  export default {
    oncreate () {
      on('animateNavPart1', this, ({ fromPage, toPage }) => {
        const { name } = this.get()
        const { reduceMotion } = this.store.get()
        if (fromPage === name && !reduceMotion) {
          this.animatePart1({ toPage })
        }
      })
      on('animateNavPart2', this, ({ toPage, fromRect, fromLi }) => {
        const { name } = this.get()
        const { reduceMotion } = this.store.get()
        if (toPage === name && !reduceMotion) {
          this.animatePart2({ fromRect, fromLi })
        }
      })
    },
    store: () => store,
    computed: {
      selected: ({ page, name }) => name === normalizePageName(page),
      ariaLabel: ({ selected, name, label, $numberOfNotifications, $numberOfFollowRequests }) => {
        const count = name === 'notifications'
          ? $numberOfNotifications
          : (name === 'community' ? $numberOfFollowRequests : 0)
        return formatIntl('intl.navItemLabel', {
          label,
          selected,
          name,
          count
        })
      },
      showBadge: ({ name, $hasNotifications, $hasFollowRequests }) => (
        (name === 'notifications' && $hasNotifications) || (name === 'community' && $hasFollowRequests)
      ),
      badgeNumber: ({ name, $numberOfNotifications, $numberOfFollowRequests }) => (
        (name === 'notifications' && $numberOfNotifications) || (name === 'community' && $numberOfFollowRequests) || 0
      )
    },
    methods: {
      onClick (e) {
        const { selected } = this.get()
        if (!selected) {
          return
        }
        if (scrollToTop(/* smooth */ true)) {
          e.preventDefault()
          e.stopPropagation()
        }
      },
      animatePart1 ({ toPage }) {
        const { bottomNav } = this.store.get()
        const indicator = this.refs.indicator
        mark('animateNavPart1 gBCR')
        const fromRect = indicator.getBoundingClientRect()
        stop('animateNavPart1 gBCR')
        indicator.firstElementChild.style.transition = 'unset'
        indicator.firstElementChild.style.transform = `translateY(${bottomNav ? '-' : ''}100%)!important`
        emit('animateNavPart2', { fromRect, fromLi: indicator.closest('li'), toPage })
        requestAnimationFrame(() => {
          indicator.firstElementChild.style.transition = ''
          indicator.firstElementChild.style.transform = ''
        })
      },
      async animatePart2 ({ fromRect, fromLi }) {
        const { indicator } = this.refs
        const toLi = indicator.closest('li')
        const affected = [fromLi, toLi]
        const labels = affected.map(e => e.querySelector('.nav-icon-and-label'))
        for (const label of labels) {
          label.style.transition = 'unset'
        }
        indicator.firstElementChild.style.transition = 'unset'
        indicator.firstElementChild.style.transform = 'translateY(0%)!important'
        mark('animateNavPart2 gBCR')
        const toRect = indicator.getBoundingClientRect()
        stop('animateNavPart2 gBCR')
        const animation = indicator.animate([
          { left: (fromRect.left - toRect.left) + 'px', right: (toRect.right - fromRect.right) + 'px' },
          { left: '0rem', right: '0rem' }
        ], {
          duration: 333,
          iterations: 1,
          easing: 'ease-in-out'
        })
        let lastAnimationFrame
        const update = () => {
          const { bottomNav } = this.store.get()
          lastAnimationFrame = null
          const currentRect = indicator.getBoundingClientRect()
          for (let i = 0; i < affected.length; i++) {
            const tabRect = affected[i].getBoundingClientRect()
            const intersectionWidth = Math.max(0, Math.min(currentRect.right, tabRect.right) - Math.max(currentRect.left, tabRect.left))
            labels[i].style.transform = `translateY(calc(${intersectionWidth / tabRect.width || 0} * var(--nav-indicator-height) * ${bottomNav ? '' : '-'}0.5))`
          }
          lastAnimationFrame = requestAnimationFrame(update)
        }
        update()
        await animation.finished
        if (lastAnimationFrame) cancelAnimationFrame(lastAnimationFrame)
        for (const label of labels) {
          label.style.transform = ''
          label.style.transition = ''
        }
        indicator.firstElementChild.style.transition = ''
        indicator.firstElementChild.style.transform = ''
      }
    },
    components: {
      NavItemIcon
    }
  }
</script>